import React from 'react';
import PageHeader from '../components/PageHeader';
import Callout from '../components/Callout';
import { motion } from 'framer-motion';

const Sharing = () => {
  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="prose-custom">
      <PageHeader
        badge="Stage 5"
        title="Sharing, Archiving & Credit"
        subtitle="The downstream stage that makes reproducibility durable: putting your data, code, and materials somewhere permanent, citable, and legally reusable."
      />

      <p>
        Sharing is where reproducibility stops being a private discipline and becomes a public good.
        The goal is not merely to <em>release</em> your research objects but to archive them so they
        remain findable, retrievable, and reusable years later—by other labs, by reviewers, and by
        your future self. This is the operational payoff of the <strong>FAIR principles</strong>
        introduced under Data &amp; Management.
      </p>

      <h2>Choose a Persistent Repository, Not a Personal Website</h2>
      <p>
        Personal or lab websites disappear; GitHub repositories can be deleted or rewritten. Durable
        sharing means depositing in an archival repository that issues a persistent identifier (a DOI)
        and commits to long-term preservation. General-purpose options include
        <a href="https://osf.io/" target="_blank" rel="noopener noreferrer"> OSF</a>,
        <a href="https://zenodo.org/" target="_blank" rel="noopener noreferrer"> Zenodo</a> (operated by CERN),
        <a href="https://datadryad.org/" target="_blank" rel="noopener noreferrer"> Dryad</a>, and
        <a href="https://figshare.com/" target="_blank" rel="noopener noreferrer"> Figshare</a>. For many
        data types, a <strong>discipline-specific repository</strong> is expected instead (for example,
        sequence and structure archives in the life sciences). Registries like
        <a href="https://www.re3data.org/" target="_blank" rel="noopener noreferrer"> re3data</a> and
        <a href="https://fairsharing.org/" target="_blank" rel="noopener noreferrer"> FAIRsharing</a>
        help you find the right repository and standards for your field.
      </p>

      <h2>Archiving Code: From GitHub to a DOI</h2>
      <p>
        GitHub is excellent for developing and collaborating on code, but a live repository is a moving
        target, not a citable record. Zenodo integrates directly with GitHub: you can archive a specific
        release, which mints a DOI pointing to that exact, frozen snapshot. Cite <em>that</em> DOI in
        your paper so reviewers and readers run the same code you did—not whatever the repository looks
        like today.
      </p>

      <h2>Licensing: No License Means "No Reuse"</h2>
      <p>
        This is the step researchers most often skip, and it silently defeats the whole point of
        sharing. Under copyright, work with <strong>no explicit license is "all rights reserved"</strong>—
        others cannot legally reuse it even if it is publicly downloadable.
      </p>
      <ul>
        <li>
          <strong>Data and documents:</strong> apply a
          <a href="https://creativecommons.org/" target="_blank" rel="noopener noreferrer"> Creative Commons</a>
          license (CC0 or CC-BY are common for maximum reuse).
        </li>
        <li>
          <strong>Software and code:</strong> apply an OSI-approved open-source license (e.g., MIT,
          Apache-2.0, or GPL). The guide at
          <a href="https://choosealicense.com/" target="_blank" rel="noopener noreferrer"> choosealicense.com</a>
          walks through the trade-offs.
        </li>
      </ul>

      <Callout type="warning" title="The most common sharing mistake">
        Depositing data or code with no license attached. It looks shared, but it is not legally
        reusable. Add the license <em>before</em> you publish the DOI.
      </Callout>

      <h2>Credit: Make Your Outputs Citable</h2>
      <p>
        Sharing well should also be recognized. Use an
        <a href="https://orcid.org/" target="_blank" rel="noopener noreferrer"> ORCID</a> identifier to
        disambiguate yourself as an author across datasets, software, and papers. Add a
        <code>CITATION.cff</code> file to your repository so others cite your software correctly, and
        treat datasets and software as first-class, citable research outputs in their own right—so the
        effort of doing reproducible work accrues credit rather than disappearing into a methods
        footnote.
      </p>
    </motion.div>
  );
};

export default Sharing;
